import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { CountryMaster } from "../../types/assetTypes";
import { getCountryAssetColumns } from "../../const/AssertData";
import { useEffect } from "react";
import { Typography } from "@mui/material";
import { MenuItem } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';




import {
  getAssetCountryById,
  getCountryAssetMasterDataList,
  getLocalStoreMasterDataList,
  getRegionMasterDataList,
  saveAssetCountry,
} from "../../api/Services/masterServie";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { PAGESTART, PAGESEND } from "../../api/endpoint";
import { RootState } from "../../store/Reduxstore";


interface AssetCountryProps {
  setPathname: (path: string) => void;
}

const AssetCountry: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<CountryMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [assetCountryData, setAssetCountryData] = useState<CountryMaster>({
    currentLocationId: 0,
    countryName: "",
    localStores: [],
    regions: [],
  });

  // Memoized column definition
  const columns = useMemo(() => getCountryAssetColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditFamilyAsset(rowData.currentLocationId)}
        />
      ),
    });
  }



  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const [localStoreOptions, setLocalStoreOptions] = useState<{ id: number; name: string }[]>([]);
  const [regionOptions, setRegionOptions] = useState<{ id: number; name: string }[]>([]);


  useEffect(() => {
    const fetchLocalStores = async () => {
      try {
        const response = await getLocalStoreMasterDataList(PAGESTART, PAGESEND, {});
        if (response.status.code === 200) {
          setLocalStoreOptions(response.data.map(localstores => ({
            id: localstores.localStoreId,
            name: localstores.localStore,
          })));

        } else {
          setLocalStoreOptions([]);
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      }
    };

    fetchLocalStores();
  }, []);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await getRegionMasterDataList(PAGESTART, PAGESEND, {}); // Provide default values
        if (response.status.code === 200) {
          setRegionOptions(response.data.map(regions => ({
            id: regions.regionId,
            name: regions.regionName,
          })));

        } else {
          setRegionOptions([]);
          CustomMessage(response.status.message, "error", enqueueSnackbar);
        }
      } catch (error: any) {
        CustomMessage(error.message, "error", enqueueSnackbar);
      }
    };

    fetchRegions();
  }, []);


  const handleEditFamilyAsset = async (assetCountryAssetCountryId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getAssetCountryById(assetCountryAssetCountryId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setAssetCountryData({
          currentLocationId: data.currentLocationId,
          countryName: data.countryName,
          localStores: data.localstores,
          regions: data.regions,// <-- mapping here
        });
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };



  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } = await getCountryAssetMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setAssetCountryData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setAssetCountryData({
      currentLocationId: 0,
      countryName: "",
      localStores: [],
      regions: [] // Add this
    });
  };


  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (assetCountryData.countryName === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveAssetCountry(assetCountryData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Masters List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditFamilyAsset(rowData.currentLocationId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Country"
        visible={openDialog}
        onHide={handleClear}
        //allowedRoles={["Admin", "Master List Write"]}
        style={{ overflow: 'visible' }}
        contentStyle={{ overflow: 'visible' }}
      >
        <form>
          {/* Country Name Input */}
          <div className="mb-3">
            <h6>Asset Family</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={assetCountryData.countryName}
              name="countryName"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required

            />
          </div>

          {/* Local Stores Selection */}

          {/* Local Stores Selection */}
          <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
            <Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
              Select Local Stores
            </Typography>
            <MultiSelect
              value={assetCountryData.localStores}
              options={localStoreOptions}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Local Stores"
              style={{ width: "100%", marginTop: "4px" }}
              appendTo="self"
              filter // 🔍 Enable search filter
              onChange={(e: MultiSelectChangeEvent) =>
                setAssetCountryData((prev) => ({
                  ...prev,
                  localStores: e.value as number[],
                }))
              }
            />
          </Box>
          <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
            <Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
              Select Regions
            </Typography>
            <MultiSelect
              value={assetCountryData.regions}
              options={regionOptions}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Regions"
              style={{ width: "100%", marginTop: "4px" }}
              appendTo="self"
              filter // 🔍 Enable search filter
              onChange={(e: MultiSelectChangeEvent) =>
                setAssetCountryData((prev) => ({
                  ...prev,
                  regions: e.value as number[],
                }))
              }
            />
          </Box>


          {/* <Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
<Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
  Select Local Stores
</Typography>
<MultiSelect
  value={assetCountryData.localStores}
  options={localStoreOptions}
  optionLabel="name"
  optionValue="id"
  placeholder="Select Local Stores"
  style={{ width: "100%", marginTop: "4px" }}
  appendTo="self" // or "body"
  onChange={(e: MultiSelectChangeEvent) =>
    setAssetCountryData((prev) => ({
      ...prev,
      localStores: e.value as number[],
    }))
  }
/>
</Box>


<Box sx={{ mt: 2, mb: 1, fontWeight: "bold", position: "relative" }}>
<Typography variant="body1" sx={{ mt: 2, mb: 0, fontWeight: "bold" }}>
  Select Regions
</Typography>
<MultiSelect
  value={assetCountryData.regions}
  options={regionOptions}
  optionLabel="name"
  optionValue="id"
  placeholder="Select Regions"
  style={{ width: "100%", marginTop: "4px" }}
  appendTo="self" // or "body"
  onChange={(e: MultiSelectChangeEvent) =>
    setAssetCountryData((prev) => ({
      ...prev,
      regions: e.value as number[],
    }))
  }
/>
</Box> */}


          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 4,
            }}
          >
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"

            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Masters List Write"]}>
              Submit
            </ReusablePrimeButton>
          </Box>
        </form>

      </ReusablePrimeReactDialog>

    </>
  );
};

export default AssetCountry;
